import { Moment } from 'moment';
import {
  containerConditionsLabel,
  containerHistoriesComment,
  SalesFeeContainers,
  SalesFeeStatusToLabels,
  StoragePricingContainers,
  StoragePricingStatusToLabels,
} from '../const';
import { containerTypesLabel } from '../utils';

export type MongoDocId = { id: string };

export interface OptionType {
  label: string;
  value: string;
  disabled?: boolean;
}

export enum AccountStatus {
  ACTIVE = 'ACTIVATE',
  DEACTIVATED = 'DEACTIVATE',
}

export enum BelongsTo {
  SUPER_ADMIN = 'SUPER_ADMIN',
  YARD_COMPANY = 'YARD_COMPANY',
  YARD = 'YARD',
  CLIENT = 'CLIENT',
}

type UserType =
  | 'YARD_COMPANY_ADMIN'
  | 'YARD_COMPANY_EMPLOYEE'
  | 'YARD_COMPANY_VIEWER'
  | 'YARD_MANAGER'
  | 'YARD_DISPATCHER'
  | 'YARD_CRANE_OPERATOR'
  | 'CLIENT_MANAGER'
  | 'CLIENT_DISPATCHER';

export type Roles =
  | 'SUPER_ADMIN'
  | 'YARD_COMPANY_ADMIN'
  | 'YARD_COMPANY_EMPLOYEE'
  | 'YARD_COMPANY_VIEWER'
  | 'YARD_MANAGER'
  | 'YARD_DISPATCHER'
  | 'CLIENT_MANAGER'
  | 'CLIENT_DISPATCHER';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  canTrade: boolean;
  accountStatus: AccountStatus;
  belongsTo: BelongsTo;
  yardCompanyEmployee: YardCompanyEmployee;
  yardEmployee: YardEmployee;
  clientEmployee: ClientEmployee;
  createdAt: Date;
  updatedAt: Date;
  type: UserType & any;
}

export interface CreateCompanyProps {
  city: string;
  companyLegalName: string;
  contactPersonFirstName: string;
  contactPersonLastName: string;
  country: string;
  companyEmail: string;
  companyPhoneNumber: string;
  email: string;
  phoneNumber: string;
  streetAddress: string;
  zipCode: string;
  name?: string;
  code?: string;
  yardCompanyId?: string;
  yardId?: string;
  category?: string;
  gateOutAllowed?: boolean;
  blocks: Block[];
  messengers: Messenger[];
  belongsTo: 'YARD_COMPANIES' | 'YARD' | 'CLIENT';
}

export interface SendSignUpMailerProps extends Partial<CreateCompanyProps> {
  containerNumber: string;
  price: number;
  category?: string;
}

interface YardCompanyEmployee {
  yardCompanyId: string;
  role: string;
  yardCompany: { legalName: string };
}

interface YardEmployee {
  yardId: string;
  role: string;
  yard: { name: string };
  canCreateClient?: boolean;
}

export type ClientCategory = 'VENDOR' | 'TRADER' | 'NONE';

interface ClientEmployee {
  clientId: string;
  role: string;
  client: { code: string; legalName: string; category: ClientCategory };
}

export interface NotificationGetAllQuery {
  data: {
    id: string;
    category: 'SIGN_UP' | 'SIGN_UP_BUY_CONTAINER' | 'FORGOT_PASSWORD' | 'CLIENT_INTEREST_IN_YARD';
    message: string;
    createdAt: string;
    readAt?: string;
    data: any;
  }[];
  totalCount: number;
}

export interface ImageObjType {
  action: 'upload' | 'delete';
  file: any;
  name?: string;
}

export type SortDirection = 'asc' | 'desc';

export type ContainerType =
  | '20DC'
  | '20HC'
  | '20RF'
  | '40DC'
  | '40HC'
  | '40RF'
  | '40OT'
  | '40FR'
  | '45HC';

export type ContainerStatus =
  | 'IN_TRANSIT'
  | 'GATED_IN'
  | 'GATED_OUT'
  | 'GATE_OUT_REQUESTED'
  | 'NOT_FOUND';

export interface ContainerGetAllQuery {
  data: {
    number: string;
    id: string;
    clientYardId: string;
    type: keyof typeof containerTypesLabel;
    condition?: keyof typeof containerConditionsLabel;
    canTrade: boolean;
    isLoaded?: boolean | null;
    isHold?: boolean;
    block?: string;
    row?: string;
    slot?: string;
    tier?: string;
    status: ContainerStatus;
    comment?: string;
    images: { url: string }[];
    documents: { url: string }[];
    clientYard: {
      client: {
        code: string;
        id: string;
        legalName?: string;
      };
      yard: {
        id: string;
        address: any;
        name: string;
        yardCompany?: {
          legalName: string;
          logo?: {
            url: string;
            name: string;
          } | null;
        };
      };
    };
    gateIn: {
      id?: string;
      status?: 'ACTIVE' | 'EXPIRED' | 'COMPLETED' | 'DELETED';
      releaseNumber?: string;
      comment?: string;
      transport?: string;
      driver?: {
        firstName?: string;
        lastName?: string;
        licensePlateNumber: string;
        phoneNumber?: string;
      };
      completedAt: number | string | Date;
    };
    gateOut: {
      id?: string;
      completedAt: number | string | Date;
      status: 'ACTIVE' | 'LOADING_ALLOWED' | 'EXPIRED' | 'LOADED' | 'COMPLETED';
      releaseNumber?: string;
      comment?: string;
      transport?: string;
      images?: { url: string }[];
      driver?: {
        firstName?: string;
        lastName?: string;
        licensePlateNumber?: string;
        phoneNumber?: string;
      };
    };
    sale?: { status: 'FOR_SALE' | 'AWAITING_PAYMENT' | 'SOLD' };
    _count: {
      repair: number;
    };
  }[];
  totalCount: number;
}

export interface ContainerHistory {
  user: {
    firstName: string;
    lastName: string;
  };
  action: keyof typeof containerHistoriesComment;
  comment?: string;
  createdAt: number | string | Date;
}

export interface ContainerGetOneQuery
  extends Omit<ContainerGetAllQuery['data'][number], 'histories'> {
  histories?: ContainerHistory[];
  gatedInByName?: string;
  gatedOutByName?: string;
  loadedByName?: string;
  holdByName?: string;
}

export interface GateInGetAllQuery {
  data: {
    id: string;
    clientYardId: string;
    number: string;
    type: keyof typeof containerTypesLabel;
    condition: keyof typeof containerConditionsLabel;
    clientYard: {
      client: {
        code: string;
      };
    };
    gateIn: {
      status: 'ACTIVE' | 'EXPIRED' | 'COMPLETED' | 'DELETED';
      driver: {
        firstName: string;
        lastName: string;
        licensePlateNumber: string;
      };
      createdAt: number | string | Date;
      completedAt: number | string | Date;
      validFrom: number | string | Date;
      validTo: number | string | Date;
    };
  }[];
  totalCount: number;
}

export interface GateOutGetAllQuery {
  data: {
    id: string;
    number: string;
    type: keyof typeof containerTypesLabel;
    clientYard: {
      client: {
        code: string;
      };
    };
    gateOut: {
      authorizedLicensePlateNumbers: string[];
      comment: string;
      status: 'ACTIVE' | 'LOADING_ALLOWED' | 'EXPIRED' | 'LOADED' | 'COMPLETED';
      driver: {
        licensePlateNumber: string;
        firstName: string;
        lastName: string;
      };
      createdAt: number | string | Date;
      completedAt: number | string | Date;
      validFrom: number | string | Date;
      validTo: number | string | Date;
    };
  }[];
  totalCount: number;
  secondData: {
    id: string;
    containerIds: string[];
    containerNumbers: string[];
    clientYard: {
      clientId: string;
      yardId: string;
      client: {
        code: string;
      };
    };
    authorizedLicensePlateNumbers: string[];
    condition?: keyof typeof containerConditionsLabel;
    requestedCount?: number;
    type?: ContainerType;
    status: 'ACTIVE' | 'LOADING_ALLOWED';
    validFrom: string;
    validTo: string;
    createdAt: string;
    updatedAt: string;
  }[];
  secondTotalCount: number;
}

export type RepairStatus = 'DRAFT' | 'PENDING' | 'ACTIVE' | 'REJECTED' | 'COMPLETED';

export interface RepairCreateProps {
  id: string;
  description: string;
}

export interface RepairGetAllQuery {
  data: {
    id: string;
    invoiceLineId?: string;
    status: RepairStatus;
    price: number;
    currencyCode: CurrencyCode;
    description: string;
    expectedDurationDays: number;
    beforeImages: { url: string; name: string }[];
    afterImages: { url: string; name: string }[];
    createdAt: string;
    acceptedAt: string;
    container: {
      number: string;
      clientYard: {
        client: {
          code: string;
        };
      };
    };
    completedByName: string;
  }[];
  totalCount: number;
}

export interface RepairGetAllGroupQuery {
  data: {
    clientYard: {
      client: {
        code: string;
      };
    };
    id: string;
    number: string;
    createdAt: string;
    images: {
      url: string;
    }[];
    repair: {
      invoiceLineId?: string;
      price: number;
      currencyCode: CurrencyCode;
      status: RepairStatus;
    }[];
    customRepair: {
      price: string;
      pendingCount: number;
      activeCount: number;
      uncreatedInvoiceCount: number;
    };
  }[];
  totalCount: number;
}

export interface RepairUpdateProps {
  id: string;
  price: number;
  currencyCode: CurrencyCode;
  expectedDurationDays: number;
  description: string;
}

export interface RepairConfirmProps {
  id: string;
  isConfirm: boolean;
}

export interface RepairUpdateImagesProps {
  id: string;
  photos: ImageObjType[];
  action: 'before' | 'after';
}

export interface InventoryGetAllQuery {
  data: {
    id: string;
    yardId: string;
    yardName: string;
    total: number;
    notFound: number;
    extraFound: number;
    found: number;
    progress: number;
    status: 'IN_PROGRESS' | 'COMPLETED';
    assignedTo: {
      userId: string;
      blocks: BlockWithMinMaxOptional[];
    }[];
  }[];
  totalCount: number;
}

export interface InventoryGetOneQuery {
  data: {
    status: 'IN_PROGRESS' | 'COMPLETED';
    yard: {
      id: string;
      name: string;
    };
    total: number;
    notFound: number;
    extraFound: number;
    found: number;
    progress: number;
    assignedTo: {
      blocks: BlockWithMinMaxOptional[];
      status: 'IN_PROGRESS' | 'COMPLETED';
      firstName: string;
      lastName: string;
      completedCount: number;
    }[];
    items: {
      clientYard?: {
        client: {
          code: string;
        };
      };
      number: string;
      status: 'NOT_FOUND' | 'EXTRA_FOUND' | 'FOUND';
      clientStatus: 'NOT_FOUND' | 'EXTRA_FOUND' | 'FOUND';
      comment: string;
      unchecked?: {
        type: ContainerType;
        block: string;
        row: number;
        slot?: number;
      };
      checked?: {
        type: ContainerType;
        block: string;
        row: number;
        slot?: number;
        image: { url: string }[];
      };
      assignedTo?: {
        user: {
          firstName: string;
          lastName: string;
        };
      };
      updatedAt: string;
    }[];
  };
}

export type PricingDetails = [
  'loaded',
  'empty',
  'loadedCraneIn',
  'loadedCraneOut',
  'emptyCraneIn',
  'emptyCraneOut',
];

export interface FiltersWithPaginationProps {
  take: number;
  skip: number;
  [key: string]: any;
}

export type FiltersSetStatesProp = (key: string, value?: any, isEmptyFilter?: boolean) => void;

export type CurrencyCode = 'USD' | 'EUR' | 'RUB' | 'UZS' | 'TRY' | 'KZT';

export interface CurrencyGetAllResponse {
  data: {
    code: CurrencyCode;
    unitsPerUSD: number;
  }[];
}

export interface StoragePricingGetAllRequest {
  params: FiltersWithPaginationProps;
}

export interface StoragePricingGetAllResponse {
  data: {
    id: string;
    storagePricing: {
      id: string;
      activePricingId: string;
      pricingStatus: keyof typeof StoragePricingStatusToLabels;
      freeDays: number;
      activationDate: string;
      pricing: {
        id: string;
        freeDays: number;
        activationDate: string;
      }[];
    };
    client: {
      code: string;
    };
    yard: {
      name: string;
      yardCompany: {
        legalName: string;
      };
    };
  }[];
  totalCount: number;
}

export interface StoragePricingGetOneResponse {
  data: {
    id: string;
    clientYard: StoragePricingUpdateModalCache['clientYard'];
    pricing: StoragePricingUpdateModalCache['activePricing'][];
  };
}

export interface StoragePricingsAddCache {
  yardName: string;
  clientName: string;
}

export interface StoragePricingAddFormProps extends StoragePricingContainers {
  yardName: string;
  clientName: string;
  effectiveDate: string | Moment | Date;
  freeDays: number;
  currencyCode: CurrencyCode;
  onSubmitClick: () => void;
}

export interface StoragePricingAddMutationProps extends StoragePricingContainers {
  id: string;
  freeDays: number;
  effectiveDate: Moment;
  currencyCode: CurrencyCode;
}

export interface StoragePricingAddMutationRequest {
  id: string;
  activationDate: string;
  freeDays: number;
  currencyCode: CurrencyCode;
  containers: {
    type: string;
    loaded: number;
    empty: number;
    loadedCraneIn: number;
    loadedCraneOut: number;
    emptyCraneIn: number;
    emptyCraneOut: number;
  }[];
}

export interface StoragePricingUpdateModalCache {
  id: string;
  refetch: () => void;
  open: () => void;
  activePricing: {
    id: string;
    containers: {
      type: ContainerType;
      loaded: number;
      empty: number;
      loadedCraneIn: number;
      loadedCraneOut: number;
      emptyCraneIn: number;
      emptyCraneOut: number;
    }[];
    activationDate: string;
    freeDays: number;
    currencyCode: CurrencyCode;
  };
  clientYard: {
    client: {
      code: string;
    };
    yard: {
      name: string;
    };
  };
}

export interface StoragePricingUpdateMutationProps extends StoragePricingContainers {
  id: string;
  activePricingId: string;
  freeDays: number;
  currencyCode: CurrencyCode;
}

export interface StoragePricingUpdateMutationRequest {
  id: string;
  activePricingId: string;
  freeDays: number;
  currencyCode: CurrencyCode;
  containers: {
    type: string;
    loaded: number;
    empty: number;
    loadedCraneIn: number;
    loadedCraneOut: number;
    emptyCraneIn: number;
    emptyCraneOut: number;
  }[];
}

export interface ReportsGetContainersQueryProps {
  yardCompanyId?: string;
  yardId?: string;
  clientId?: string;
  number?: string;
  startDate?: string | Date;
  endDate?: string | Date;
}

export interface ReportsGetContainersResponse {
  data: {
    allDaysInTerminal: number;
    allPrice: number;
    daysInTerminal: number;
    daysInTerminalPrice: number;
    freeDays: number;
    payingDays: number;
    gateIn: { completedAt: string };
    gateInCranePrice: number;
    gateOut?: { completedAt?: string };
    gateOutCranePrice: number;
    isLoaded: null | boolean;
    isLoadedText: 'LOADED' | 'EMPTY';
    number: string;
    type: ContainerType;
    condition: keyof typeof containerConditionsLabel;
    status: ContainerStatus;
  }[];
  totalCount: number;
  defaultCurrencyCode: CurrencyCode;
}

export type InvoiceCreateBody = {
  type: 'STORAGE' | 'CRANE' | 'OTHER' | 'ALL';
  periodEnd: string | Date;
};

export type InvoiceCreateMode = 'AUTO' | 'MANUAL';

export interface InvoiceGetAllQuery {
  data: {
    id: string;
    invoiceNumber: string;
    type: 'STORAGE' | 'CRANE' | 'OTHER' | 'ALL';
    periodStart: string | Date;
    periodEnd: string | Date;
    totalAmount: number;
    currencyCode: CurrencyCode;
    status: 'DRAFT' | 'PENDING' | 'PAID';
    mode: 'AUTO' | 'MANUAL';
    canPay?: boolean;
    canDelete?: boolean;
  }[];
  totalCount: number;
  minDate: {
    ALL: string | Date;
    STORAGE: string | Date;
    CRANE: string | Date;
    OTHER: string | Date;
  };
  createMode: InvoiceCreateMode;
  clientYard: {
    yard: {
      name: string;
    };
    client: {
      code: string;
    };
  };
}

export interface UserVisitGetAllQuery {
  data: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
    userId: string;
    createdAt: string;
    lastVisitedAt: string;
    ip: string;
    country: string;
    countryCode: any;
    deviceName: string;
    role: string;
    clientCode: string;
    status: 'ONLINE' | 'OFFLINE';
  }[];
  totalCount: number;
}

export interface UserVisitGetOneQuery {
  data: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
    };
    createdAt: string;
    lastVisitedAt: string;
    ip: string;
    country: string;
    countryCode: any;
    deviceName: string;
    role: string;
    clientCode: string;
    actions: {
      createdAt: Date;
      actionName: string;
    }[];
  }[];
  totalCount: number;
}

export interface SaleGetAllByClientYardQuery {
  data: (Omit<ContainerGetAllQuery['data'][number], 'sale'> & {
    sale: {
      finalPrice: number;
      forSaleDate: number | string | Date;
    };
    isMyContainer: boolean;
  })[];
  totalCount: number;
}

export interface SaleGetAllQuery {
  data: (Omit<ContainerGetAllQuery['data'][number], 'sale'> & {
    sale: {
      status: 'FOR_SALE' | 'AWAITING_PAYMENT' | 'SOLD_INST' | 'SOLD' | 'NOT_FOUND';
      forSaleDate: number | string | Date;
      ownerSetPrice: number;
      finalPrice: number;
      allowCounterOffers: boolean;
      description: string;
      createdByName: string;

      feeType: FeeType;
      feeValue: number;
    };
    offer?: { price: [number]; status: OfferStatus }[];
    isMyContainer: boolean;
  })[];
  totalCount: number;
}

export interface SaleStatsGetAllQuery {
  data: {
    client: { code: string };
    containerForSaleCount: number;
    containerSoldCount: number;
  }[];
  totalCount: number;
}

export interface SaleCreateProps {
  id: string;
  ownerSetPrice: number;
  allowCounterOffers: boolean;
  description: string;
}

export type SaleUpdateProps = SaleCreateProps;

export enum OfferStatus {
  NOT_SELECTED = 'NOT_SELECTED',
  CANCELLED_BY_SELLER = 'CANCELLED_BY_SELLER',
  CANCELLED_BY_BUYER = 'CANCELLED_BY_BUYER',
  CANCELLED_BY_ADMIN = 'CANCELLED_BY_ADMIN',
  CANCELLED_BY_SYSTEM = 'CANCELLED_BY_SYSTEM',
  RECEIVED = 'RECEIVED',
  REJECTED = 'REJECTED',
  ACCEPTED = 'ACCEPTED',
  SOLD_INST = 'SOLD_INST',
  CLOSED = 'CLOSED',
  NOT_FOUND = 'NOT_FOUND',
}

export interface OffersGetAllQuery {
  data: {
    id: string;
    price: number[];
    fee: { type: FeeType; value: number }[];
    ownerPrice: number[];
    createdAt: Date;
    closedAt?: Date;
    status: OfferStatus;

    container: {
      images: { url: string }[];
      number: string;
      type: keyof typeof containerTypesLabel;
    };
    buyerClient: {
      code: string;
    };
    sellerClient: {
      code: string;
    };
  }[];
  totalCount: number;
}

export interface SendOfferProps {
  id: string;
  price: number;
}

export interface OfferConfirmProps {
  id: string;
  isConfirm: boolean;
  isInstallment?: boolean;
}

export enum FeeType {
  FIXED = 'FIXED',
  PERCENTAGE = 'PERCENTAGE',
}

export interface SalesFeeGetAllResponse {
  data: {
    id: string;
    salesFee: {
      id: string;
      activeFeeRecordId: string;
      feeStatus: keyof typeof SalesFeeStatusToLabels;
      activationDate: string;
      feeRecords: {
        id: string;
        activationDate: string;
      }[];
    };
    name: string;
    yardCompany: {
      legalName: string;
    };
  }[];
  totalCount: number;
}

export interface SalesFeeGetOneResponse {
  data: {
    id: string;
    activeFeeRecordId: string;
    yard: SalesFeeUpdateModalCache['yard'];
    feeRecords: ({ isActive: boolean } & SalesFeeUpdateModalCache['activeFee'])[];
  };
}

export type SalesFeeGetActiveFeeResponse = {
  data: {
    containers: { type: ContainerType; fee: string }[];
    feeType: FeeType;
  };
};

export interface SalesFeeAddCache {
  yardName: string;
}

export interface SalesFeeAddFormProps extends SalesFeeContainers {
  yardName: string;
  effectiveDate: string | Moment | Date;
  feeType: FeeType;
  onSubmitClick: () => void;
}

export interface SalesFeeAddMutationProps extends SalesFeeContainers {
  id: string;
  effectiveDate: Moment;
  feeType: FeeType;
}

export interface SalesFeeAddMutationRequest {
  id: string;
  activationDate: string;
  feeType: FeeType;
  containers: {
    type: string;
    fee: number;
  }[];
}

export interface SalesFeeUpdateModalCache {
  id: string;
  refetch: () => void;
  open: () => void;
  activeFee: {
    id: string;
    containers: {
      type: ContainerType;
      fee: number;
    }[];
    activationDate: string;
    feeType: FeeType;
  };
  yard: {
    name: string;
  };
}

export interface SalesFeeUpdateMutationProps extends SalesFeeContainers {
  id: string;
  activeFeeRecordId: string;
  feeType: FeeType;
}

export interface SalesFeeUpdateMutationRequest {
  id: string;
  activeFeeRecordId: string;
  feeType: FeeType;
  containers: {
    type: string;
    fee: number;
  }[];
}

export interface Block {
  id?: string;
  block: string;
  rowLength: string;
  slotLength: string;
}

export interface BlockWithMinMax {
  id: string;
  block: string;
  rowMin: string;
  rowMax: string;
  slotMin?: string;
  slotMax?: string;
}

export interface BlockWithMinMaxOptional {
  block: string;
  rowMin?: string;
  rowMax?: string;
  slotMin?: string;
  slotMax?: string;
}

export interface YardGetBlocksQuery {
  data: {
    block: string;
    rows: number[];
    slots: number[];
  }[];
}

export interface Messenger {
  label: string;
  identifier: string;
}

export interface EmployeeGetAllQuery {
  data: {
    user: { accountStatus: 'ACTIVE' | 'DEACTIVATED' } & Omit<User, 'accountStatus'>;
    firstName: string;
    lastName: string;
    role:
      | 'YardCompanyAdmin'
      | 'YardCompanyEmployee'
      | 'YardCompanyViewer'
      | 'YardManager'
      | 'YardDispatcher'
      | 'YardCraneOperator'
      | 'ClientManager'
      | 'ClientDispatcher';
    yardCompany: { legalName: string };
    yard: { name: string };
    client: { legalName: string };
    companyName: string;
    canCreateClient?: boolean;
  }[];
  totalCount: number;
}

export interface ChatGetAllQuery {
  data: {
    isSupport: boolean;
    id: string;
    type: 'SUPER_ADMIN_CLIENT';
    client?: {
      code: string;
      legalName: string;
    };
    _count: {
      messages: number;
    };
    createdAt: Date;
    updatedAt: Date;
  }[];
  totalCount: number;
}

export interface ChatGetOneQuery {
  data: {
    id: string;
    type: 'SUPER_ADMIN_CLIENT';
    client: {
      code: string;
      legalName: string;
    };
    createdAt: Date;
    updatedAt: Date;
    messages: {
      id: string;
      content: string;
      senderId: string;
      sender: {
        id: string;
        firstName: string;
        lastName: string;
      };
      isRead: boolean;
      isMine: boolean;
      readAt: Date;
      createdAt: Date;
    }[];
  };
}

export interface OverviewQuery {
  data: {
    gateInCompletedCount: number[];
    gateInCompletedCountOld: number[];
    gateOutCompletedCount: number[];
    gateOutCompletedCountOld: number[];
    containerCount: number;
    containerCountByStatus: {
      gateInCount: number;
      onHoldCount: number;
      forSaleCount: number;
      repairCount: number;
      gateOutRequestedCount: number;
    };
    containerCountByType: {
      [key in ContainerType]: number;
    };
    depotOccupancy: {
      totalCapacity: number;
      occupied: number;
      available: number;
      occupancyByArea: {
        id: string;
        block: string;
        occupied: number;
        capacity: number;
      }[];
      occupancyByContainerType: {
        [key in ContainerType]: number;
      } & {
        empty: number;
      };
      occupancyTrend: {
        date: string;
        value: number;
      }[];
    };
    containerByLocation: {
      name: string;
      lng: number;
      lat: number;
      country: string;
      city: string;
      value: number;
    }[];
    lastGateCompleted: {
      completedAt: string;
      number: string;
      status: ContainerStatus;
    }[];
    operations: {
      gateOutActiveCount: number;
      gateInExpiredCount: number;
      gateOutExpiredCount: number;
      repairCount: number;
    };
    lastGateActive: {
      createdAt: string;
      validTo: string;
      authorizedLicensePlateNumbers: string[];
      driverLicensePlateNumber: string;
      driverFirstName: string;
      driverLastName: string;
      gateStatus: 'ACTIVE' | 'LOADING_ALLOWED' | 'LOADED' | 'EXPIRED';
      number: string;
      status: ContainerStatus;
      isLoaded: boolean;
    }[];
    alerts: {
      containerWithoutLocationCount: number;
      repairClientNotAssignedCount: number;
      repairClientAssignedCount: number;
      heldMoreDaysContainersCount: number;
    };
  };
}

export interface IdProps {
  id: string;
}
